import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Search, CheckCircle2, XCircle, Loader2, PlayCircle } from 'lucide-react';

/**
 * Automation Diagnostic
 * ---------------------
 * One-click view into "why did / didn't a specific user receive an automation".
 * Wraps the existing GET /api/automations/debug/{email} endpoint into a readable
 * table so admins can self-diagnose recurring "missing action" bugs without
 * writing curl or reading logs.
 */
const AdminAutomationDebug = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [runningNow, setRunningNow] = useState(false);

  const runDebug = async () => {
    const target = email.trim();
    if (!target) {
      toast.error('Enter a user email');
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const res = await apiClient.get(`/automations/debug/${encodeURIComponent(target)}`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch diagnostic');
    } finally {
      setLoading(false);
    }
  };

  const runAllAutomationsNow = async () => {
    setRunningNow(true);
    try {
      const res = await apiClient.post('/automations/run');
      toast.success(
        `Automations run: ${res.data?.notifications_sent || 0} notifications, ${res.data?.emails_queued || 0} emails queued.`
      );
      // Auto-refresh diagnostic after a short delay
      setTimeout(() => runDebug(), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to run automations');
    } finally {
      setRunningNow(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-stone-900 p-6" data-testid="automation-debug-page">
      <div className="max-w-5xl mx-auto">
        <Link
          to="/admin/system-health"
          className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 mb-4 text-sm"
          data-testid="back-link"
        >
          <ArrowLeft size={16} /> Back to System Health
        </Link>

        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold">Automation Diagnostic</h1>
          <p className="text-stone-600 mt-1">
            Look up any user&apos;s automation eligibility. Shows exactly why each active automation would or would not fire for them, using the same logic the background task uses.
          </p>
        </div>

        <Card className="bg-white/60 border-stone-200 mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runDebug()}
                placeholder="user@example.com"
                className="bg-stone-50 border-stone-200 flex-1"
                data-testid="debug-email-input"
                autoFocus
              />
              <Button
                onClick={runDebug}
                disabled={loading}
                className="bg-[#EFB342] text-black hover:bg-[#D99F30]"
                data-testid="run-debug-btn"
              >
                {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Search size={16} className="mr-2" />}
                Diagnose
              </Button>
              <Button
                variant="outline"
                onClick={runAllAutomationsNow}
                disabled={runningNow}
                className="border-stone-300"
                data-testid="run-automations-now-btn"
                title="Run all active automations right now (bypasses 24h background cycle)"
              >
                {runningNow ? <Loader2 className="animate-spin mr-2" size={16} /> : <PlayCircle size={16} className="mr-2" />}
                Run automations now
              </Button>
            </div>
            <p className="text-xs text-stone-500 mt-3">
              Tip: enter the exact email as stored on the user&apos;s profile. Search is case-insensitive.
            </p>
          </CardContent>
        </Card>

        {data && <DiagnosticResult data={data} />}
      </div>
    </div>
  );
};

const DiagnosticResult = ({ data }) => {
  const user = data.user || {};
  // Backend key is `automations` (was mistakenly read as `automation_status`
  // which is why the page showed "Active automations (0)" even when the
  // background task WAS finding matching automations).
  const automations = data.automations || data.automation_status || [];

  return (
    <div className="space-y-6" data-testid="diagnostic-result">
      {/* User summary */}
      <Card className="bg-white/60 border-stone-200">
        <CardHeader>
          <CardTitle className="text-lg">{user.name || '(no name)'} · <span className="text-stone-600 font-normal">{user.email}</span></CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <StatBox label="user_type" value={user.user_type || '—'} />
          <StatBox label="is_active" value={String(user.is_active ?? true)} tone={user.is_active === false ? 'bad' : 'ok'} />
          <StatBox label="last check-in" value={user.last_check_in || 'never'} tone={user.last_check_in ? 'ok' : 'warn'} />
          <StatBox label="days inactive" value={user.days_inactive ?? '—'} />
        </CardContent>
      </Card>

      {/* Automations table */}
      <Card className="bg-white/60 border-stone-200">
        <CardHeader>
          <CardTitle className="text-lg">Active automations ({automations.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {automations.length === 0 && (
            <p className="text-stone-500 text-sm">No active automations to evaluate.</p>
          )}
          {automations.map((row, idx) => (
            <AutomationRow key={idx} row={row} />
          ))}
        </CardContent>
      </Card>

      <details className="text-xs text-stone-500">
        <summary className="cursor-pointer hover:text-stone-700">Raw JSON</summary>
        <pre className="mt-2 p-3 bg-stone-50 border border-stone-200 rounded overflow-x-auto">
          {JSON.stringify(data, null, 2)}
        </pre>
      </details>
    </div>
  );
};

const AutomationRow = ({ row }) => {
  const fires = row.would_trigger === true;
  return (
    <div
      className={`rounded-lg border p-4 ${fires ? 'border-emerald-600/40 bg-emerald-500/5' : 'border-stone-200 bg-stone-50/60'}`}
      data-testid={`automation-row-${row.name?.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-stone-900">{row.name}</p>
          <p className="text-xs text-stone-500 mt-0.5">
            trigger: <span className="text-stone-700">{row.trigger_type}</span>
            {row.trigger_days != null && <> · threshold: <span className="text-stone-700">{row.trigger_days} days</span></>}
            {row.action_name && <> · action: <span className="text-stone-700">{row.action_name}</span></>}
          </p>
        </div>
        {fires ? (
          <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 whitespace-nowrap">
            <CheckCircle2 size={12} className="mr-1" /> Would fire
          </Badge>
        ) : (
          <Badge className="bg-stone-100 text-stone-600 border border-stone-300 whitespace-nowrap">
            <XCircle size={12} className="mr-1" /> Blocked
          </Badge>
        )}
      </div>

      <p className={`text-sm mt-3 ${fires ? 'text-emerald-200' : 'text-stone-700'}`}>
        {row.reason || '(no reason recorded)'}
      </p>

      {(row.user_last_did_action_on || row.days_missing_current_month != null) && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          {row.user_last_did_action_on !== undefined && (
            <FieldPill label="last did action" value={row.user_last_did_action_on || 'never'} />
          )}
          {row.days_missing_current_month !== undefined && (
            <FieldPill label="days missing" value={row.days_missing_current_month} />
          )}
          {row.missing_notified_for_date !== undefined && (
            <FieldPill label="notified for" value={row.missing_notified_for_date || 'never'} />
          )}
          {row.safeguard_recent_checkin_date !== undefined && row.safeguard_recent_checkin_date !== null && (
            <FieldPill label="newer check-in" value={row.safeguard_recent_checkin_date} tone="warn" />
          )}
        </div>
      )}
    </div>
  );
};

const StatBox = ({ label, value, tone }) => {
  const toneClass = tone === 'bad' ? 'text-red-400' : tone === 'warn' ? 'text-amber-400' : 'text-stone-900';
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-stone-500">{label}</p>
      <p className={`text-sm font-medium mt-0.5 ${toneClass}`}>{value}</p>
    </div>
  );
};

const FieldPill = ({ label, value, tone }) => {
  const toneClass = tone === 'warn' ? 'text-amber-300 border-amber-500/30' : 'text-stone-700 border-stone-300';
  return (
    <div className={`rounded border px-2 py-1 ${toneClass}`}>
      <span className="text-stone-500 text-[10px] uppercase tracking-wide block leading-none mb-0.5">{label}</span>
      <span className="text-xs font-medium break-all">{value}</span>
    </div>
  );
};

export default AdminAutomationDebug;
